import { useState, useEffect, useRef } from 'react'

// ── Wisdom for Two — ~99 cards (rotates every 4h) ───────────────
const QUOTES = [
  // ── BIBLICAL (16 = 30%) ──────────────────────────────────────
  {
    text: "Love is patient, love is kind. It does not envy, it does not boast, it is not proud. It does not dishonor others, it is not self-seeking, it is not easily angered, it keeps no record of wrongs.",
    reference: "1 Corinthians 13:4–5",
    author: "The Bible",
    application: "Read this slowly and replace the word love with your own name. That is the test. Where you fall short is not a condemnation — it is a map. Work on one quality at a time. Real love is practiced, not felt.",
  },
  {
    text: "Two are better than one, because they have a good return for their labor. If either of them falls down, one can help the other up. But pity anyone who falls and has no one to help them up.",
    reference: "Ecclesiastes 4:9–10",
    author: "The Bible",
    application: "The measure of your partnership is not how you celebrate together — it is how quickly you run toward each other when one of you has fallen. Do not wait to be invited. Just show up.",
  },
  {
    text: "Where you go I will go, and where you stay I will stay. Your people will be my people.",
    reference: "Ruth 1:16",
    author: "The Bible",
    application: "These words were spoken between two women, not lovers — which tells you something profound. Commitment is a posture, not just a feeling. Adopt your partner's people, their concerns, their burdens. Become one household, not two people sharing an address.",
  },
  {
    text: "A cord of three strands is not quickly broken.",
    reference: "Ecclesiastes 4:12",
    author: "The Bible",
    application: "Marriages that last are not held together by compatibility alone. The third strand — whether that is shared faith, shared values, or something bigger than yourselves — is what keeps the cord from snapping when the other two are under strain. Find your third strand and protect it.",
  },
  {
    text: "Be completely humble and gentle; be patient, bearing with one another in love.",
    reference: "Ephesians 4:2",
    author: "Paul the Apostle",
    application: "Bearing with someone means you see the flaw clearly and choose to stay anyway. It is not turning a blind eye — it is refusing to let imperfection be grounds for withdrawal. Name what bothers you. Address it honestly. Then let it go completely.",
  },
  {
    text: "Above all, love each other deeply, because love covers over a multitude of sins.",
    reference: "1 Peter 4:8",
    author: "Peter the Apostle",
    application: "Love that covers is not love that pretends the wound did not happen. It is love that says: I see what you did, it hurt me, and I am choosing not to carry this as ammunition. The difference between a covered wound and a buried grievance is that one heals and the other festers.",
  },
  {
    text: "Do not let any unwholesome talk come out of your mouths, but only what is helpful for building others up according to their needs.",
    reference: "Ephesians 4:29",
    author: "Paul the Apostle",
    application: "The harshest things said in marriage are usually said in the first sixty seconds of a fight. Make a rule together: when voices rise, lower yours. The words said in anger do not disappear when the argument does. They live in the body of the person who received them.",
  },
  {
    text: "Do not let the sun go down while you are still angry, and do not give the devil a foothold.",
    reference: "Ephesians 4:26–27",
    author: "Paul the Apostle",
    application: "This is not a demand to resolve every argument before midnight. It is a warning against letting bitterness calcify overnight. A deliberate touch before sleep — even a hand placed quietly on a shoulder — signals: we are not done talking, but we are not enemies. That signal matters more than you know.",
  },
  {
    text: "I found the one my soul loves.",
    reference: "Song of Solomon 3:4",
    author: "The Bible",
    application: "Finding was not the end — it was the beginning. The real question is whether you keep finding each other as the years change you both. Make it a practice: stay curious about who your spouse is becoming. The person they are at forty will be more interesting than who they were on your wedding day.",
  },
  {
    text: "He who finds a wife finds what is good and receives favor from the LORD.",
    reference: "Proverbs 18:22",
    author: "The Bible",
    application: "Treat your marriage the way you treat something you know is rare — with gratitude, with attention, with the refusal to take it for granted. Familiarity is the slow enemy of appreciation. Fight it deliberately.",
  },
  {
    text: "That is why a man leaves his father and mother and is united to his wife, and they become one flesh.",
    reference: "Genesis 2:24",
    author: "The Bible",
    application: "Leaving is as essential as cleaving. If you have not emotionally left your family of origin — if their opinion still governs your choices more than your spouse's — you are not yet one flesh. The marriage cannot become what it should until both of you choose each other as your primary household.",
  },
  {
    text: "Be devoted to one another in love. Honor one another above yourselves.",
    reference: "Romans 12:10",
    author: "Paul the Apostle",
    application: "Honoring above yourself means your spouse's reputation matters more to you than being right in front of others. It means you do not laugh at their expense, do not weaponize their insecurities in a fight, and do not let others disrespect them while you stand silent. Guard their dignity fiercely.",
  },
  {
    text: "May your fountain be blessed, and may you rejoice in the wife of your youth.",
    reference: "Proverbs 5:18",
    author: "The Bible",
    application: "Rejoice is an active word. It requires intention. Do not wait to feel delight in your spouse — choose to look for reasons to delight in them. Speak those reasons aloud. The person who hears that they are delighted in by their partner is protected from a thousand temptations.",
  },
  {
    text: "Marriage should be honored by all, and the marriage bed kept pure, for God will judge the adulterer and all the sexually immoral.",
    reference: "Hebrews 13:4",
    author: "The Bible",
    application: "Purity in marriage is not only about faithfulness in the physical sense — it is about emotional faithfulness too. The conversations you have with a colleague that you hide from your spouse, the comparisons you make privately, the emotional intimacy you share outside your marriage — these are the cracks that precede collapse. Guard the inside of your marriage as fiercely as the outside.",
  },
  {
    text: "And over all these virtues put on love, which binds them all together in perfect unity.",
    reference: "Colossians 3:14",
    author: "Paul the Apostle",
    application: "Patience, kindness, forgiveness, humility — these are the virtues Paul lists just before this verse. They are not enough on their own. Love is what holds them together in practice. When you do not feel patient, let love make the decision. When you do not feel forgiving, let love move your hand. Act your way into the feeling.",
  },
  {
    text: "Your wife will be like a fruitful vine within your house; your children will be like olive shoots around your table.",
    reference: "Psalm 128:3",
    author: "The Bible",
    application: "A vine thrives with the right conditions — soil, sunlight, water. Your partner is not responsible for their own flourishing inside your marriage alone. You are part of the conditions. Ask yourself regularly: is what I am providing making them more or less themselves? That is the measure of how you are loving.",
  },

  // ── NON-BIBLICAL (37) ─────────────────────────────────────────
  {
    text: "Let there be spaces in your togetherness and let the winds of heaven dance between you. Love one another but make not a bond of love.",
    reference: "The Prophet",
    author: "Kahlil Gibran",
    application: "The couple that has no space between them eventually has nothing to offer each other. Keep your own friendships. Read books your spouse has not read. Have a conversation with yourself on a long walk. Then come home with something worth bringing.",
  },
  {
    text: "The beginning of love is to let those we love be perfectly themselves, and not to twist them to fit our own image.",
    reference: "No Man Is an Island",
    author: "Thomas Merton",
    application: "The most common cruelty in marriage is not dramatic betrayal — it is the slow, quiet campaign to make your spouse more convenient. Stop correcting how they load the dishwasher. Stop editing how they tell stories. You married a person, not a project.",
  },
  {
    text: "Knowing how to be alone is central to the art of loving. When we can be alone, we can be with others without using them to escape ourselves.",
    reference: "All About Love",
    author: "bell hooks",
    application: "If you cannot sit with yourself comfortably, you will demand too much from your spouse. You will use them to fill silence they cannot fill, carry anxiety that is not theirs to carry. Develop your own inner life. It will make you a far better companion.",
  },
  {
    text: "What counts in making a happy marriage is not so much how compatible you are, but how you deal with incompatibility.",
    reference: "Essay",
    author: "Leo Tolstoy",
    application: "You will be incompatible in dozens of ways for the rest of your lives. The question is never whether you clash — it is whether you have developed the tools to navigate the clashes without damage. Incompatibility handled with skill is far stronger than compatibility that has never been tested.",
  },
  {
    text: "There is no remedy for love but to love more.",
    reference: "Journal",
    author: "Henry David Thoreau",
    application: "When the marriage feels cold or distant, the instinct is to withhold — to protect yourself by giving less. This instinct is almost always wrong. Give more. Cook the meal. Send the message. Make the gesture. Love generates more love when it is the one acting, not the one waiting.",
  },
  {
    text: "To love another person is to see the face of God.",
    reference: "Les Misérables",
    author: "Victor Hugo",
    application: "You are practicing something sacred in the ordinary moments — in the patience you offer during a bad week, in the way you listen when you are tired, in the choice to stay soft when hardness would be easier. Do not let the ordinariness of it make you think it is small.",
  },
  {
    text: "Have enough courage to trust love one more time and always one more time.",
    reference: "Interview",
    author: "Maya Angelou",
    application: "After every wound — a thoughtless word, a broken promise, a season of distance — love asks for one more attempt. The couples who survive decades are not the ones who never hurt each other. They are the ones who kept choosing repair over retreat, every single time.",
  },
  {
    text: "What greater thing is there for two human souls than to feel that they are joined for life — to strengthen each other in all labour, to rest on each other in all sorrow, to minister to each other in all pain.",
    reference: "Adam Bede",
    author: "George Eliot",
    application: "Your greatest work is not what you build in your career — it is the safe harbour you construct together. Make your home feel like genuine rest. Ask yourself once a week: when my spouse walks through that door, what do they feel? Relief, or tension?",
  },
  {
    text: "Ultimately the bond of all companionship, whether in marriage or friendship, is conversation.",
    reference: "De Profundis",
    author: "Oscar Wilde",
    application: "Put the phones away at dinner. Not sometimes — as a rule. Ask one real question a day, not about logistics. What are you afraid of lately? What is something you have not told me? What do you want that you have stopped asking for? Conversation is the oxygen of intimacy. Without it, everything quietly suffocates.",
  },
  {
    text: "A successful marriage requires falling in love many times, always with the same person.",
    reference: "Essay",
    author: "Mignon McLaughlin",
    application: "You will fall out of love and back into it multiple times across your marriage. This is not failure — it is the nature of long commitment. The couples who panic when the feeling dims, and the ones who understand this cycle and actively court each other back into love, have very different outcomes.",
  },
  {
    text: "Marriage is not a noun; it's a verb. It isn't something you get. It's something you do. It's the way you love your partner every day.",
    reference: "Essay",
    author: "Barbara De Angelis",
    application: "The wedding was the ceremony. The marriage is the daily practice — and practice means showing up even on the days when you do not feel like it, when you are tired, when you feel misunderstood. It means doing the next right thing even before you feel the motivation to do it.",
  },
  {
    text: "If you want to go fast, go alone. If you want to go far, go together.",
    reference: "African Proverb",
    author: "Traditional",
    application: "Some seasons will require you to slow down for your partner. Do not frame this as sacrifice — frame it as strategy. The version of success you build together over decades will be larger, more stable, and more meaningful than anything you could have built alone at speed.",
  },
  {
    text: "Rain does not fall on one roof alone.",
    reference: "Cameroonian Proverb",
    author: "Traditional",
    application: "Hardship will come for both of you — financial pressure, illness, loss, disappointment. When the rain falls, the worst thing you can do is face different walls. The posture matters: turned toward each other, under the same roof, making decisions as one unit. Not two separate people enduring the same storm.",
  },
  {
    text: "A family is like a forest: when you are outside it is dense; when you are inside you see that each tree has its place.",
    reference: "West African Proverb",
    author: "Traditional",
    application: "Every couple's inside looks different from their outside. Stop comparing your interior life to other couples' curated exteriors. You only see their tree line — not their root system, their droughts, their pruning seasons. Tend your own forest.",
  },
  {
    text: "Until the lion learns to write, every story will glorify the hunter.",
    reference: "Igbo Proverb",
    author: "Traditional",
    application: "Tell your own story as a couple. Refuse to let your marriage be narrated by family members, culture, or comparison. Decide together what you value, what success looks like for you, what kind of home you are building. Write your own story before someone else writes it for you.",
  },
  {
    text: "Sticks in a bundle are unbreakable.",
    reference: "Kenyan Proverb",
    author: "Traditional",
    application: "Protect your unity publicly. Do not let others find the gaps between you — not your family, not your friends, not social media. A couple who presents a united front is nearly impossible to destabilize. Disagree in private. Agree in public. Then return to the disagreement privately with more softness.",
  },
  {
    text: "I love you not only for what you are, but for what I am when I am with you.",
    reference: "Essay",
    author: "Roy Croft",
    application: "Pay close attention to who you become in your partner's presence. If you are more generous, more honest, more at ease — you have found something rare. Equally, your job is to be the kind of presence that draws out the best in them. Create the conditions for each other to be your fullest selves.",
  },
  {
    text: "A great marriage is not when the perfect couple comes together. It is when an imperfect couple learns to enjoy their differences.",
    reference: "Essay",
    author: "Dave Meurer",
    application: "Your partner's most frustrating quality often contains the seed of something you genuinely need. The slow, methodical one brings care to your impulsiveness. The expressive one brings warmth to your reserve. Stop trying to correct what is different and start asking what it is offering you.",
  },
  {
    text: "The goal in marriage is not to think alike, but to think together.",
    reference: "The Eternal Now",
    author: "Robert Dodds",
    application: "Decision-making in marriage is not a vote — it is a conversation that continues until both of you can genuinely live with the outcome. Not tolerate. Live with. The decision that one person buries resentment to accept will surface later, always at the worst time.",
  },
  {
    text: "Love does not consist in gazing at each other, but in looking outward together in the same direction.",
    reference: "Wind, Sand and Stars",
    author: "Antoine de Saint-Exupéry",
    application: "Find a shared vision that is bigger than your home — a cause, a community, a legacy you are building together. Couples who only look inward at each other eventually run out of things to see. Couples who build toward something together have a horizon that keeps pulling them forward.",
  },
  {
    text: "Love is a canvas furnished by nature and embroidered by imagination.",
    reference: "Essay",
    author: "Voltaire",
    application: "You began with the canvas — attraction, promise, potential. Now you embroider it with memory, ritual, private language, inside jokes, the specific way you have learned to love this particular person. The embroidery is the work of decades. Never stop adding threads.",
  },
  {
    text: "We loved with a love that was more than love.",
    reference: "Annabel Lee",
    author: "Edgar Allan Poe",
    application: "A marriage that deepens is one where both people are committed to becoming more — more patient, more known, more honest — not to staying the same. Comfort is not the goal. Growth inside safety is the goal. Those are different things.",
  },
  {
    text: "You are my sun, my moon, and all my stars.",
    reference: "Poem",
    author: "E.E. Cummings",
    application: "Tell your partner specifically what they are to you. Not in the abstract — specifically. 'You are the reason I still believe this is possible.' 'You make hard things feel manageable.' 'You are the person I want to tell things to first.' Say it out loud. Regularly. Do not assume they know.",
  },
  {
    text: "Whatever souls are made of, his and mine are the same.",
    reference: "Wuthering Heights",
    author: "Emily Brontë",
    application: "When your partner tells you something they have never told anyone else, receive it like the gift it is. Do not minimise it, do not immediately share it, do not file it away as ammunition. Being fully known and not rejected is one of the deepest experiences a human being can have. Be worthy of it.",
  },
  {
    text: "The meeting of two personalities is like the contact of two chemical substances: if there is any reaction, both are transformed.",
    reference: "Modern Man in Search of a Soul",
    author: "Carl Jung",
    application: "You are supposed to change each other — not control, but genuinely transform. Pay attention to who you are becoming through this marriage. If the marriage is working, both of you should be more whole at year ten than you were at year one. If neither of you is growing, something needs to change.",
  },
  {
    text: "Keep love in your heart. A life without it is like a sunless garden when the flowers are dead.",
    reference: "Essay",
    author: "Oscar Wilde",
    application: "Resentment, silence, and contempt are weeds that do not announce themselves. They take root slowly and quietly, then choke everything else. Tend the garden deliberately — pull the small grievances before they become large ones. A quick honest conversation at week one prevents a devastating conversation at year five.",
  },
  {
    text: "Grow old along with me — the best is yet to be.",
    reference: "Rabbi Ben Ezra",
    author: "Robert Browning",
    application: "In a culture obsessed with early romance, choose the long view. The richest, most layered parts of your marriage may be fifteen years from now — when you have built something together that neither of you could have imagined at the beginning. Stay long enough to find out what you are building.",
  },
  {
    text: "To be fully seen by somebody, then, and be loved anyhow — this is a human offering that can border on miraculous.",
    reference: "Eat, Pray, Love",
    author: "Elizabeth Gilbert",
    application: "Let yourself be fully known — not the version you perform, the real one, with the fears and the failures and the parts you are not proud of. And extend that same permission to your partner without flinching. Intimacy is not proximity. It is the willingness to be seen.",
  },
  {
    text: "I am not afraid of storms, for I am learning how to sail my ship.",
    reference: "Little Women",
    author: "Louisa May Alcott",
    application: "Conflict is not the problem — mishandled conflict is. Learn together how to argue without wounding, how to repair without pride, how to return to warmth after coldness. The couples who navigate storms well are not lucky. They practiced the navigation.",
  },
  {
    text: "The best thing to hold onto in life is each other.",
    reference: "Interview",
    author: "Audrey Hepburn",
    application: "In seasons of confusion, grief, or uncertainty — reach for each other before anything else. Not your phone, not your parent, not your friend. Your partner is your first port. Honour that by being reachable. By putting the phone down when they walk into the room. By being present when you are physically present.",
  },
  {
    text: "Love is friendship that has caught fire. It is quiet understanding, mutual confidence, sharing, and forgiving. It is loyalty through good and bad times.",
    reference: "Essay",
    author: "Ann Landers",
    application: "When the initial fire dims — and it will — let friendship hold you. The couples who last are the ones who genuinely like each other, who would choose each other's company even without obligation. Protect the friendship. Go somewhere new together. Laugh at things. Be interested in the same things sometimes.",
  },
  {
    text: "The secret of a happy marriage is finding the right person. You know they're right if you love to be with them all the time.",
    reference: "Essay",
    author: "Julia Child",
    application: "Julia Child's marriage to Paul lasted until his death — they cooked, travelled, and worked together for decades. The lesson is not that you must be together constantly, but that you build a life you both genuinely want to be in. Design your marriage on purpose. Do not just let it happen to you.",
  },
  {
    text: "My most brilliant achievement was my ability to be able to persuade my wife to marry me.",
    reference: "Interview",
    author: "Winston Churchill",
    application: "Churchill married Clementine in 1908 and she was his closest advisor through the darkest years of his leadership. Great partners do not shrink your ambition — they make it possible. Recognise what your spouse enables in you. Tell them. Often.",
  },
  {
    text: "A happy marriage is a long conversation which always seems too short.",
    reference: "Essay",
    author: "André Maurois",
    application: "The most dangerous silence in a marriage is not the comfortable kind — it is the kind where both people have stopped sharing the real things because they expect not to be heard. Create regular space for real conversation. Not logistics. Not schedules. The actual interior life of the person you married.",
  },
  {
    text: "In marriage, a man becomes slack and selfish, and undergoes a fatty degeneration of his moral being. In marriage, a woman becomes something very hard and very aggressive.",
    reference: "Virginibus Puerisque",
    author: "Robert Louis Stevenson",
    application: "Stevenson was being provocative — but the warning underneath is real. Comfort breeds complacency. The version of yourself you bring to the relationship in the beginning must be maintained by intention as the years pass. Do not stop being curious, interesting, or kind simply because your partner is no longer going anywhere.",
  },

  // ── WISDOM FOR TWO — 48-card set (Scripture KJV + counselors & wise voices) ──
  {
    text: "Let every man be swift to hear, slow to speak, slow to wrath.",
    reference: "James 1:19",
    author: "The Bible",
    application: "Most fights aren't about the dishes or the money — they're about not feeling heard. Before you defend yourself in the next argument, try repeating back what your spouse just said until they say, 'Yes, that's it.' You'll be amazed how much heat leaves the room when one person finally feels understood instead of corrected.",
  },
  {
    text: "Be ye angry, and sin not: let not the sun go down upon your wrath.",
    reference: "Ephesians 4:26",
    author: "The Bible",
    application: "Anger isn't the enemy — what you do with it overnight is. Resentment left unspoken doesn't disappear; it hardens into distance. Make a rule: you can table a fight, but you don't go silent for days. A simple 'I'm still upset, but I'm not leaving this — let's finish tomorrow' keeps the bridge standing while the storm passes.",
  },
  {
    text: "A soft answer turneth away wrath: but grievous words stir up anger.",
    reference: "Proverbs 15:1",
    author: "The Bible",
    application: "The tone you choose in the first ten seconds decides how the next ten minutes go. Sarcasm, eye-rolls, and 'here we go again' are small, but they're acid. Try lowering your voice instead of raising it. You are not trying to win the sentence; you're trying to keep the person.",
  },
  {
    text: "Charity suffereth long, and is kind... seeketh not her own, is not easily provoked, thinketh no evil.",
    reference: "1 Corinthians 13:4–5",
    author: "The Bible",
    application: "Notice that every word here is an action, not a feeling. Love is patient — it does. Love is kind — it does. On the days the warm emotion is gone, you can still be patient and kind, and the feeling usually follows the choice back home. Don't wait to feel loving before you act loving.",
  },
  {
    text: "A good marriage is the union of two good forgivers.",
    reference: "Quote",
    author: "Ruth Bell Graham",
    application: "You did not marry a finished person, and neither did your spouse. Keeping score of every wound guarantees you'll run out of grace long before you run out of years. Forgiveness isn't pretending it didn't hurt — it's deciding the marriage is worth more than the debt. Become the kind of person who is hard to offend and quick to release.",
  },
  {
    text: "A threefold cord is not quickly broken.",
    reference: "Ecclesiastes 4:12",
    author: "The Bible",
    application: "Two people pulling against life will fray. Two people and God woven together hold. When faith becomes an afterthought — squeezed out by fatigue, work, and screens — the cord quietly thins. Pray together, even badly, even for ninety seconds. A couple that kneels together has a strength that doesn't come from either of them alone.",
  },
  {
    text: "Therefore shall a man leave his father and his mother, and shall cleave unto his wife: and they shall be one flesh.",
    reference: "Genesis 2:24",
    author: "The Bible",
    application: "'One flesh' is the opposite of two separate lives sharing an address. If your money is secret, your phone is private, and your plans are solo, you're roommates with rings. Move toward transparency on purpose — shared calendars, shared accounts, shared dreams. Oneness is built, not declared at a wedding.",
  },
  {
    text: "Keep thy heart with all diligence; for out of it are the issues of life.",
    reference: "Proverbs 4:23",
    author: "The Bible",
    application: "Pornography promises intimacy and delivers isolation. It rewires desire toward a screen that never asks anything of you, and slowly makes a real spouse feel like work. If this is in your home, name it to a trusted person and to each other — secrecy is the soil it grows in. Recovery is possible, but only in the light. Guard the heart on purpose, because everything downstream flows from it.",
  },
  {
    text: "I made a covenant with mine eyes; why then should I think upon a maid?",
    reference: "Job 31:1",
    author: "The Bible",
    application: "Faithfulness isn't just refusing an affair — it's the daily discipline of where your eyes and thoughts go. The endless scroll trains you to compare, to fantasize, to feel entitled to 'better.' Make a covenant with your own eyes: filters on the devices, accountability with a friend, and a habit of turning your attention back toward the one you actually vowed to. Small private choices protect the public covenant.",
  },
  {
    text: "For the love of money is the root of all evil.",
    reference: "1 Timothy 6:10",
    author: "The Bible",
    application: "Money rarely breaks a marriage by itself — the love of it does, and so does fear of it. When one chases status and the other hoards out of anxiety, the bank account becomes a battlefield. Get on the same side of the table: write the numbers down together, no shame, no hiding. You're not opponents negotiating; you're partners building.",
  },
  {
    text: "Be content with such things as ye have.",
    reference: "Hebrews 13:5",
    author: "The Bible",
    application: "The fastest way to resent your spouse is to measure them against someone else's highlight reel. The couple with the bigger house, the trips, the perfect captions — you are comparing your behind-the-scenes to their stage. Practice gratitude out loud: name three things your spouse and your life already give you. Contentment is not settling; it's refusing to let envy rob a good marriage.",
  },
  {
    text: "For this cause shall a man leave his father and mother, and cleave to his wife.",
    reference: "Mark 10:7",
    author: "The Bible",
    application: "You honor your parents; you do not let them run your marriage. 'Leaving' means your spouse becomes your first loyalty, and 'cleaving' means you defend that bond — even from people you love. When family oversteps, the two of you decide the boundary together and one voice delivers it. A marriage with no walls around it gets trampled by everyone's good intentions.",
  },
  {
    text: "Except the LORD build the house, they labour in vain that build it.",
    reference: "Psalm 127:1",
    author: "The Bible",
    application: "You can have the wedding, the house, the plan — and still be building on sand if it's all effort and no surrender. Striving harder won't fix what only grace can hold. Invite God into the ordinary: the budget, the bedroom, the calendar, the conflict. A house built with Him survives weather that levels the ones built without Him.",
  },
  {
    text: "Let nothing be done through strife or vainglory; but in lowliness of mind let each esteem other better than themselves.",
    reference: "Philippians 2:3",
    author: "The Bible",
    application: "Most marital deadlock is two people each waiting to be served first. The cure isn't a 50/50 ledger — it's both reaching for 100. Ask the question that disarms selfishness: 'What would make today easier for you?' When two people compete to out-serve each other, nobody loses.",
  },
  {
    text: "He that walketh with wise men shall be wise: but a companion of fools shall be destroyed.",
    reference: "Proverbs 13:20",
    author: "The Bible",
    application: "Show me your closest friends and I'll show you where your marriage is headed. Friends who mock commitment, glamorize cheating, or pull you out of the home week after week are termites in the foundation. Build friendships — as individuals and as a couple — with people who root for your marriage. Surround yourselves with couples who are still choosing each other after twenty years.",
  },
  {
    text: "Let none deal treacherously against the wife of his youth.",
    reference: "Malachi 2:15",
    author: "The Bible",
    application: "Treachery rarely begins with a betrayal; it begins with neglect — the slow turning of attention elsewhere. The person you married in your youth deserves your pursuit in every season, not just the courtship. Keep dating them. Keep choosing them when someone newer, easier, or more flattering appears. Faithfulness is a thousand small 'no's that protect one big 'yes.'",
  },
  {
    text: "A marriage is made or broken in the small daily moments when one partner reaches out and the other chooses to turn toward them instead of away.",
    reference: "The Seven Principles for Making Marriage Work",
    author: "John Gottman",
    application: "Connection isn't built on grand gestures — it's built on bids. The 'look at this,' the sigh across the room, the 'how was your day.' Each one is a tiny invitation. Turn toward it: look up, answer, ask a follow-up. Couples who ignore these small bids drift; couples who catch them stay woven together. Put the phone down when your spouse speaks — that single habit changes a marriage.",
  },
  {
    text: "Contempt — eye-rolling, mockery, name-calling — is the single greatest predictor that a marriage will not survive.",
    reference: "Research on couples",
    author: "John Gottman",
    application: "You can disagree hard and still be fine. What erodes a marriage is the sneer behind the words — talking down to your spouse, treating them as beneath you. Catch contempt in yourself before it becomes a habit. Replace it with respect even mid-fight: attack the problem, never the person. A spouse who feels looked down on slowly stops coming close.",
  },
  {
    text: "Love is best received in the language a person can actually feel — words, time, gifts, service, or touch — not the one that comes easiest to you.",
    reference: "The 5 Love Languages",
    author: "Gary Chapman",
    application: "You may be pouring love through a faucet your spouse isn't standing under. You buy gifts; they crave your time. You offer help; they long to be held. Stop loving them the way you'd want to be loved and learn their dialect. Ask directly: 'When do you feel most loved by me?' Then do more of that, even when it costs you.",
  },
  {
    text: "What if marriage was designed to make us holy at least as much as to make us happy?",
    reference: "Sacred Marriage",
    author: "Gary Thomas",
    application: "If you only stay while it makes you happy, you'll leave the first hard winter. Marriage is also a furnace that burns off your selfishness, your pride, your need to be right. The friction that annoys you is often God's sandpaper, smoothing you. Let your spouse's differences grow you instead of just irritating you. Happiness becomes deeper when it stops being the only goal.",
  },
  {
    text: "She most longs to feel loved; he most longs to feel respected — and when either feels starved, the cycle of conflict spins.",
    reference: "Love & Respect",
    author: "Emerson Eggerichs",
    application: "A wife criticized feels unloved and withdraws; a husband disrespected feels unloved too and shuts down. Round and round it goes. Break the spin: husbands, lead with affection even when you're annoyed; wives, lead with respect even when you're disappointed. Give your spouse the very thing they fear losing, especially when they least deserve it.",
  },
  {
    text: "A budget is telling your money where to go instead of wondering where it went.",
    reference: "Financial Peace",
    author: "Dave Ramsey",
    application: "Most couples were never taught how money works — so they fight about it instead of managing it. Poor financial education, not poverty, sinks many homes. Sit down monthly and give every shilling a job: needs, savings, giving, fun. Read one money book together this year. Money handled in the dark breeds suspicion; money handled in the open builds trust.",
  },
  {
    text: "It is not your love that sustains the marriage — it is the marriage, the covenant, that sustains your love.",
    reference: "Wedding Sermon",
    author: "Dietrich Bonhoeffer",
    application: "Feelings are real but unreliable; they ebb and surge with sleep, stress, and seasons. If you build only on emotion, you'll quit the first month it runs dry. The covenant — the promise you made — is the riverbank that holds the water even when the current slows. On the days you don't feel married, stay married. The feeling comes home.",
  },
  {
    text: "Love, in the deepest sense, is not merely a feeling — it is a steady act of the will, strengthened by habit.",
    reference: "Mere Christianity",
    author: "C.S. Lewis",
    application: "Waiting to 'feel in love' before you act loving has the order backwards. Do the loving thing — serve, forgive, show up — and the warmth tends to follow the deed. The couples who last aren't the ones who never lost the feeling; they're the ones who kept acting in love until the feeling returned. Will it, then watch it grow.",
  },
  {
    text: "Healthy boundaries don't push your spouse away — they protect the intimacy that closeness without limits would crush.",
    reference: "Boundaries in Marriage",
    author: "Cloud & Townsend",
    application: "A marriage with no boundaries — around work, phones, in-laws, friends, spending — slowly loses its center. Saying 'this hour is ours, no screens' isn't controlling; it's protective. Decide together what gets to enter your marriage and what stays out. Good fences around a marriage keep the right things in and the wrong things out.",
  },
  {
    text: "Many couples are disappointed not by their marriage but by their unspoken, unrealistic picture of what marriage would be.",
    reference: "Saving Your Marriage Before It Starts",
    author: "Les & Leslie Parrott",
    application: "Half of marital hurt is a silent expectation that was never agreed to — about chores, sex, money, holidays, in-laws, who does what. Your spouse can't meet a rule they were never told. Say the quiet parts out loud: 'Here's what I assumed marriage would be — what did you assume?' You can't negotiate a contract neither of you has read.",
  },
  {
    text: "Forgiveness is an act of the will, and the will can function regardless of the temperature of the heart.",
    reference: "Quote",
    author: "Corrie ten Boom",
    application: "You won't always feel like forgiving — and you don't have to feel it to choose it. Decide to release the offense, then ask the feelings to catch up. Unforgiveness is a poison you drink hoping your spouse will suffer. Lay the debt down, again if you must, until your heart finally agrees with your decision.",
  },
  {
    text: "A marriage thrives when both partners remember they are on the same side, fighting the problem — not each other.",
    reference: "Quote",
    author: "Zig Ziglar",
    application: "The shift from 'me vs. you' to 'us vs. the problem' changes everything. Sit on the same side of the table, literally, when a hard issue comes up. The debt, the schedule, the in-law tension — put it across from both of you. Opponents try to win; teammates try to solve.",
  },
  {
    text: "Comparison is the thief of joy.",
    reference: "Attributed",
    author: "Theodore Roosevelt (attributed)",
    application: "Social media hands you a magnifying glass for everyone else's best moments and a mirror for your own worst ones. The anniversary posts, the surprise trips, the flawless couples — none of them show the 3 a.m. arguments. Stop auditioning your marriage for an audience and start tending it in private. Curate less; connect more. A marriage lived for the feed starves in real life.",
  },
  {
    text: "The greatest gift you can give your spouse is your full, undistracted attention.",
    reference: "Marriage wisdom",
    author: "Traditional",
    application: "'Phubbing' — snubbing your partner for your phone — is a quiet contempt your spouse feels even when you don't mean it. Every glance at the screen mid-conversation says 'this matters more than you.' Make zones the phone can't enter: the dinner table, the first hour home, the bed. Presence is spelled T-I-M-E, and it can't be multitasked.",
  },
  {
    text: "Can a man take fire in his bosom, and his clothes not be burned?",
    reference: "Proverbs 6:27",
    author: "The Bible",
    application: "Affairs rarely begin in a bed; they begin in a chat thread, a 'harmless' friendship, a coworker who listens better than your spouse does. If you're sharing with someone else what you should be sharing with your spouse, you're already playing with fire. The test is simple: would you be comfortable reading those messages aloud at dinner? Guard the door before the house is ablaze.",
  },
  {
    text: "The couples who last are the ones who keep choosing each other's company even when nothing obligates them to.",
    reference: "Marriage wisdom",
    author: "Traditional",
    application: "Growing apart is rarely an event; it's an accumulation of un-shared days. Careers rise, kids arrive, and one morning you're efficient co-managers who no longer know each other. Protect the friendship underneath the marriage: keep a hobby in common, ask new questions, laugh at the same things. The romance can dim and recover — but the friendship is the pilot light. Never let it go out.",
  },
  {
    text: "Better is a dinner of herbs where love is, than a stalled ox and hatred therewith.",
    reference: "Proverbs 15:17",
    author: "The Bible",
    application: "Couples crack under the pressure to have the right house in the right area before they can even breathe — and rush into debt, or into living with relatives who run their home. A modest place full of peace beats a fine address full of strain. Decide together what 'enough' looks like for this season, and protect your privacy and your peace over appearances. Build the marriage first; the bigger house can wait.",
  },
  {
    text: "Having food and raiment let us be therewith content.",
    reference: "1 Timothy 6:8",
    author: "The Bible",
    application: "Lifestyle creep is a silent marriage stressor — every raise gets swallowed by a nicer car, a costlier circle, a heavier appearance to maintain. Then the pressure to fund the image becomes the pressure that fractures the home. Define your own finish line instead of chasing other people's. The freedom to say 'we don't need that' is one of the most romantic things a couple owns.",
  },
  {
    text: "Be not deceived: evil communications corrupt good manners.",
    reference: "1 Corinthians 15:33",
    author: "The Bible",
    application: "Some friendships feed your marriage; others quietly drain it — the ones who normalize cheating, mock your spouse, or keep you out late and out of the home. You can love a friend and still limit their access to your marriage. Choose, as a couple, a few friendships that pull you toward each other, and loosen the ones that pull you apart. Your marriage is too valuable to be discipled by fools.",
  },
  {
    text: "Honour thy father and thy mother.",
    reference: "Exodus 20:12",
    author: "The Bible",
    application: "You can honor your parents and still draw a line that protects your marriage — the two are not enemies. Problems come when a parent's opinion outranks a spouse's, or when family is allowed to settle disputes that belong only to the two of you. Present a united front: decide together, then speak with one voice. Honor them from a home they don't get to govern.",
  },
  {
    text: "Let him kiss me with the kisses of his mouth: for thy love is better than wine.",
    reference: "Song of Solomon 1:2",
    author: "The Bible",
    application: "Physical and emotional intimacy don't run on autopilot — neglected, they cool, and the bedroom becomes a place of two strangers. Keep pursuing each other: flirt, touch, initiate, make space when you're tired. Talk honestly about needs and mismatches instead of letting resentment build in silence. Desire is fed by attention; it dies of being ignored.",
  },
  {
    text: "For which of you, intending to build a tower, sitteth not down first, and counteth the cost?",
    reference: "Luke 14:28",
    author: "The Bible",
    application: "Many marriages strain because someone wed for pressure — age, family, loneliness, a deadline — rather than readiness. Unhealed wounds and emotional unavailability don't stay outside the door; they move in with you. If you're not yet married, count the cost honestly: your finances, your faith, your unfinished healing. If you already are, do the inner work now rather than bleeding on a spouse who didn't cut you.",
  },
  {
    text: "Wherefore putting away lying, speak every man truth with his neighbour.",
    reference: "Ephesians 4:25",
    author: "The Bible",
    application: "Secret debts, hidden purchases, a private second life on the phone — these don't protect your spouse, they rob them of a partner who's fully present. Concealment, even of 'small' things, is a slow leak in trust. Choose the harder honesty now over the harder consequences later. A marriage can survive almost any truth told in time; it struggles to survive the lie that hid it.",
  },
  {
    text: "By love serve one another.",
    reference: "Galatians 5:13",
    author: "The Bible",
    application: "Marriage isn't 50/50; on the hard days it's 100/0 and you give anyway. The dishes you didn't dirty, the grace you don't feel, the patience you've run out of — give them as a gift, not a trade. Love that only shows up when it's deserved isn't love; it's bartering. Out-serve each other and watch the whole climate of your home change.",
  },
  {
    text: "Marriages rarely die from one catastrophe; they erode through neglect, distance, and a slow loss of intentionality.",
    reference: "Marriage wisdom",
    author: "Traditional",
    application: "No couple drifts toward closeness — drift only ever goes apart. The marriage that lasts isn't the one that avoided every storm; it's the one where two people chose each other on the ordinary Tuesdays when nothing forced them to. Build small rituals of choosing: the morning kiss, the daily check-in, the protected date. Intentionality is the whole secret.",
  },
  {
    text: "Above all things have fervent charity among yourselves: for charity shall cover the multitude of sins.",
    reference: "1 Peter 4:8",
    author: "The Bible",
    application: "Be the safest place your spouse has, not their loudest critic. Airing their flaws to friends, family, or the internet trades a moment of sympathy for a lasting wound in trust. Protect their reputation when they're not in the room. A spouse who knows you will cover them, not expose them, comes home and stays soft.",
  },
  {
    text: "Husbands, love your wives, and be not bitter against them.",
    reference: "Colossians 3:19",
    author: "The Bible",
    application: "Bitterness is love that went unspoken until it soured. When stress mounts — money, work, family — the temptation is to take it out on the nearest person, who is usually your spouse. Refuse to make your marriage the dumping ground for the day's frustration. Bring tenderness home on the very days you least feel it; that's when it matters most.",
  },
  {
    text: "The beginning of strife is as when one letteth out water: therefore leave off contention, before it be meddled with.",
    reference: "Proverbs 17:14",
    author: "The Bible",
    application: "A small disagreement is a trickle; let it run and it floods the whole house. The skill isn't avoiding conflict — it's stopping it before it escalates. Learn your own warning signs and your spouse's, and call a timeout before words are said that can't be unsaid. The wise spouse isn't the one who wins the flood; it's the one who closed the tap.",
  },
  {
    text: "Be ye kind one to another, tenderhearted, forgiving one another.",
    reference: "Ephesians 4:32",
    author: "The Bible",
    application: "Kindness in marriage is not a mood; it's a discipline you practice on the days your spouse is least lovable. Small courtesies — please, thank you, a warm greeting — are not beneath a marriage; they're the oxygen of it. Treat your spouse with at least the politeness you'd give a stranger, and usually far more. Familiarity should breed tenderness, not contempt.",
  },
  {
    text: "To be fully known and truly loved at the same time is one of life's deepest gifts.",
    reference: "The Meaning of Marriage",
    author: "Timothy Keller",
    application: "We hide the worst of ourselves for fear that being seen means being left. Real intimacy is letting your spouse see the parts you're ashamed of — and discovering they stay. Become a safe place for the truth: when your spouse confesses a struggle, respond with grace, not ammunition. A marriage where honesty is punished teaches both people to lie. Be known. Be safe to be known by.",
  },
]

// ── Helpers ─────────────────────────────────────────────────────
function getCurrentIndex() {
  return Math.floor(Math.floor(Date.now() / 1000 / 3600) / 4) % QUOTES.length
}

function getHoursUntilChange() {
  return 4 - (Math.floor(Date.now() / 1000 / 3600) % 4)
}

// ── Component ────────────────────────────────────────────────────
export default function WisdomFloat() {
  const [expanded, setExpanded]   = useState(false)
  const [pulse, setPulse]         = useState(false)
  const [mounted, setMounted]     = useState(false)

  // Drag state — initialise to bottom-right (88px above chat button)
  const defaultPos = () => ({
    left: window.innerWidth  - 24 - 48,
    top:  window.innerHeight - 88 - 48,
  })
  const [pos, setPos]       = useState(defaultPos)
  const dragStart           = useRef(null)
  const didDrag             = useRef(false)
  const isTouchTap          = useRef(false)  // blocks synthetic click after touch

  const currentIdx  = getCurrentIndex()
  const quote       = QUOTES[currentIdx]
  const hoursUntil  = getHoursUntilChange()
  // Dot pagination — show position within a window of 5
  const WINDOW      = 5
  const dotPos      = currentIdx % WINDOW

  useEffect(() => {
    const lastOpenDate  = localStorage.getItem('wisdom_last_open_date')
    const lastDismissed = localStorage.getItem('wisdom_dismissed_at')
    const lastIndex     = localStorage.getItem('wisdom_last_index')
    const today         = new Date().toDateString()

    // First open of the day — always expand
    if (lastOpenDate !== today) {
      localStorage.setItem('wisdom_last_open_date', today)
      setExpanded(true)
      setMounted(true)
      return
    }

    // First ever visit (no dismissal history)
    if (!lastDismissed) {
      setExpanded(true)
      setMounted(true)
      return
    }

    // New quote since last dismissal — pulse pill
    if (lastIndex !== null && currentIdx !== Number(lastIndex)) {
      setPulse(true)
    }

    setMounted(true)
  }, [])

  function dismiss() {
    localStorage.setItem('wisdom_dismissed_at', Date.now().toString())
    localStorage.setItem('wisdom_last_index', currentIdx.toString())
    setExpanded(false)
    setPulse(false)
  }

  function open() {
    setExpanded(true)
    setPulse(false)
  }

  // ── Drag handlers ────────────────────────────────────────────
  function onMouseDown(e) {
    if (e.button !== 0) return
    e.stopPropagation()
    didDrag.current = false
    dragStart.current = { mouseX:e.clientX, mouseY:e.clientY, btnLeft:pos.left, btnTop:pos.top }
    e.preventDefault()
  }
  function onMouseMove(e) {
    if (!dragStart.current) return
    const dx = e.clientX - dragStart.current.mouseX
    const dy = e.clientY - dragStart.current.mouseY
    if (Math.abs(dx) > 4 || Math.abs(dy) > 4) didDrag.current = true
    setPos({
      left: Math.max(8, Math.min(dragStart.current.btnLeft + dx, window.innerWidth  - 56)),
      top:  Math.max(8, Math.min(dragStart.current.btnTop  + dy, window.innerHeight - 56)),
    })
  }
  function onMouseUp() { dragStart.current = null }
  function onTouchStart(e) {
    e.stopPropagation()
    didDrag.current = false
    const t = e.touches[0]
    dragStart.current = { mouseX:t.clientX, mouseY:t.clientY, btnLeft:pos.left, btnTop:pos.top }
  }
  function onTouchMove(e) {
    if (!dragStart.current) return
    const t = e.touches[0]
    const dx = t.clientX - dragStart.current.mouseX
    const dy = t.clientY - dragStart.current.mouseY
    if (Math.abs(dx) > 4 || Math.abs(dy) > 4) didDrag.current = true
    setPos({
      left: Math.max(8, Math.min(dragStart.current.btnLeft + dx, window.innerWidth  - 56)),
      top:  Math.max(8, Math.min(dragStart.current.btnTop  + dy, window.innerHeight - 56)),
    })
    e.stopPropagation()
    e.preventDefault()
  }
  function onTouchEnd(e) {
    dragStart.current = null
    if (!didDrag.current) {
      e.stopPropagation()
      e.preventDefault()
      // Set flag so the synthetic iOS click that fires ~300ms later is ignored
      isTouchTap.current = true
      setTimeout(() => { isTouchTap.current = false }, 400)
      expanded ? dismiss() : open()
    }
  }

  // ── Card viewport clamping ────────────────────────────────────
  // Keep the expanded card fully on-screen regardless of pill position
  function cardStyle() {
    const CARD_W = window.innerWidth < 480 ? 260 : 280
    const gap    = 12
    // Where would card's left edge land if right-aligned with pill?
    const rightAlignedLeft = pos.left - (CARD_W - 48)
    let style = {
      position: 'absolute', bottom: '100%', marginBottom: gap,
      width: CARD_W, background: '#FEFCFA',
      border: '1px solid #E5E0D8', borderLeft: '3px solid #C4826A',
      borderRadius: '1.25rem', padding: '1.25rem',
      boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
    }
    if (rightAlignedLeft >= 8) {
      // Enough room to the left — right-align card with pill
      style.right = 0
    } else {
      // Not enough room — left-align card with pill, but clamp so it doesn't go off right edge
      const leftOffset = Math.min(0, window.innerWidth - 8 - pos.left - CARD_W)
      style.left = Math.max(0, leftOffset)
    }
    return style
  }

  // Re-snap to default if window resizes (phone rotation etc.)
  useEffect(() => {
    function onResize() { setPos(defaultPos()) }
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  // Global mouse move/up so drag works outside the button
  useEffect(() => {
    window.addEventListener('mousemove', onMouseMove)
    window.addEventListener('mouseup',   onMouseUp)
    return () => {
      window.removeEventListener('mousemove', onMouseMove)
      window.removeEventListener('mouseup',   onMouseUp)
    }
  })

  if (!mounted) return null

  return (
    <>
      <style>{`
        @keyframes wisdomRise {
          from { transform: translateY(16px); opacity: 0; }
          to   { transform: translateY(0);    opacity: 1; }
        }
        @keyframes wisdomPulse {
          0%   { transform: scale(1);   opacity: 0.65; }
          100% { transform: scale(1.5); opacity: 0;    }
        }
        .wisdom-card {
          animation: wisdomRise 0.38s cubic-bezier(0.34, 1.56, 0.64, 1) both;
          transform-origin: bottom right;
        }
        .wisdom-pulse-ring {
          position: absolute; inset: -5px;
          border-radius: 50%;
          border: 2px solid #C4826A;
          animation: wisdomPulse 2s ease-out infinite;
          pointer-events: none;
        }
        @media (max-width: 768px) {
          .wisdom-expanded { width: 260px !important; }
        }
      `}</style>

      {/* FIX: stopPropagation on wrapper prevents click-through to elements behind */}
      <div
        onClick={e => e.stopPropagation()}
        onMouseDown={e => e.stopPropagation()}
        onTouchEnd={e => e.stopPropagation()}
        style={{ position:'fixed', left:pos.left, top:pos.top, zIndex:49 }}>

        {/* ── EXPANDED CARD — always above the pill, clamped to viewport ── */}
        {expanded && (
          <div
            className="wisdom-card wisdom-expanded"
            style={cardStyle()}>

            {/* Header */}
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'0.75rem' }}>
              <span style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.625rem', fontWeight:700, color:'#BCBCBC', letterSpacing:'0.12em', textTransform:'uppercase' }}>
                Wisdom for Two
              </span>
              <button onClick={e => { e.stopPropagation(); dismiss() }} style={{ background:'none', border:'none', cursor:'pointer', fontFamily:'Nunito Sans, sans-serif', fontSize:'1rem', color:'#C4826A', padding:0, lineHeight:1, fontWeight:400 }}>×</button>
            </div>

            <div style={{ borderTop:'1px solid #E5E0D8', marginBottom:'0.875rem' }}/>

            {/* Quote */}
            <p style={{ fontFamily:'Georgia, serif', fontSize:'0.875rem', fontStyle:'italic', lineHeight:1.8, color:'#2A1A10', margin:'0 0 0.5rem' }}>
              "{quote.text}"
            </p>

            {/* Attribution */}
            <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.72rem', color:'#9B9B9B', margin:'0 0 0.875rem', lineHeight:1.55 }}>
              — {quote.reference}<br/>
              <span style={{ fontWeight:600 }}>{quote.author}</span>
            </p>

            <div style={{ borderTop:'1px solid #E5E0D8', marginBottom:'0.75rem' }}/>

            {/* Application */}
            <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.62rem', fontWeight:700, color:'#C4826A', letterSpacing:'0.1em', textTransform:'uppercase', margin:'0 0 0.4rem' }}>
              💡 For your marriage
            </p>
            <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.78rem', color:'#6B6B6B', lineHeight:1.7, margin:0 }}>
              {quote.application}
            </p>

            {/* Footer row */}
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginTop:'0.875rem' }}>
              {/* Pagination dots */}
              <div style={{ display:'flex', gap:5, alignItems:'center' }}>
                {Array.from({ length: WINDOW }, (_, i) => (
                  <div key={i} style={{ width: i === dotPos ? 7 : 5, height: i === dotPos ? 7 : 5, borderRadius:'50%', background: i === dotPos ? '#C4826A' : '#E5E0D8', transition:'all 0.2s' }}/>
                ))}
              </div>
              <span style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.62rem', color:'#BCBCBC' }}>
                Changes in {hoursUntil}h
              </span>
            </div>
          </div>
        )}

        {/* ── PILL — always visible, toggles card on tap ─── */}
        <button
            onMouseDown={onMouseDown}
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
            onClick={e => { e.stopPropagation(); if (!didDrag.current && !isTouchTap.current) { expanded ? dismiss() : open() } }}
            title="Wisdom for Two — tap to expand, drag to move"
            style={{ position:'relative', width:48, height:48, borderRadius:'50%', background:'#C4826A', border:'none', cursor:'grab', display:'flex', alignItems:'center', justifyContent:'center', boxShadow:'0 4px 16px rgba(196,130,106,0.35)', transition:'transform 0.15s', flexShrink:0, userSelect:'none', touchAction:'none' }}
            onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.08)'}
            onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}>
            {/* Pulse ring — shown when new content */}
            {pulse && <span className="wisdom-pulse-ring"/>}
            {/* Quotation mark */}
            <svg width="20" height="16" viewBox="0 0 20 16" fill="none" aria-hidden="true">
              <path d="M0 16V9.6C0 4.267 2.667 1.067 8 0l1.2 2C6.667 2.667 5.2 4.267 4.8 6.8H8V16H0ZM12 16V9.6C12 4.267 14.667 1.067 20 0l1.2 2C18.667 2.667 17.2 4.267 16.8 6.8H20V16H12Z" fill="white"/>
            </svg>
          </button>
      </div>
    </>
  )
}
